import type { SalesDataPoint, SalesHierarchyNode, SalesMetrics } from "../types"
import { select_primary_sales_rows } from "./forecast_measure_slice"

/**
 * Version-vs-version comparison rendered on the normal forecast grid.
 *
 * The compared version's hierarchy is flattened to per-(path, period) primary values
 * (actual wins over plan, plan `forecast` shadows other kinds — the same rule as the
 * server summary), then annotated onto the CURRENT display tree's leaves using the
 * existing compare-cell fields: `forecast_metrics` carries the compared version (the
 * baseline), `actual_metrics` carries this version, `variance` = this − compared.
 * Purely presentational: it runs on the render pipeline's copy, never on `data`,
 * so comparing cannot dirty the grid or leak into a save.
 */

export interface VersionLeafValue {
  volume_dollars: number
  cases_sold: number
  store_count: number
  units_sold: number | null
}

function path_segment(level: unknown, name: unknown): string {
  return `${String(level)}:${String(name ?? "").trim().toLowerCase()}`
}

function row_value(row: any, field: string): number {
  const flat = row?.[field]
  if (flat !== undefined && flat !== null && flat !== "") {
    const n = Number(flat)
    if (Number.isFinite(n)) return n
  }
  const nested = row?.metrics?.[field]
  const n = Number(nested ?? 0)
  return Number.isFinite(n) ? n : 0
}

function row_period_key(row: any): string {
  return String(row?.period?.period_key ?? row?.period ?? "")
}

/**
 * Shift a period key's leading year (e.g. `2028-01` → `2027-01`, `2028-W03` → `2027-W03`).
 * Used to align a compared version from another fiscal year onto this version's columns —
 * without it a 2027-vs-2028 comparison keys past itself and every compared cell reads $0.
 */
export function shift_period_year(period: string, year_shift: number): string {
  if (year_shift === 0) return period
  const year = Number(period.slice(0, 4))
  if (!Number.isFinite(year) || period.length < 4) return period
  return `${String(year + year_shift).padStart(4, "0")}${period.slice(4)}`
}

/** Year delta that maps the compared version's periods onto this version's columns. */
export function compare_period_year_shift(
  current_fiscal_year: number | null | undefined,
  compared_fiscal_year: number | null | undefined
): number {
  if (!current_fiscal_year || !compared_fiscal_year) return 0
  return current_fiscal_year - compared_fiscal_year
}

/**
 * Flatten a raw hierarchy API response (the compared version) into per-(path, period)
 * primary values. Key: `${path}|${period}` with path = `level:name` segments joined by `/`.
 * `period_year_shift` realigns another fiscal year onto this version's columns.
 */
export function build_version_leaf_value_map(
  api_nodes: unknown[],
  period_year_shift = 0
): Map<string, VersionLeafValue> {
  const out = new Map<string, VersionLeafValue>()
  walk_version_leaf_periods(api_nodes, period_year_shift, (segments: VersionPathSegment[], period: string, value: VersionLeafValue) => {
    const path = `root/${segments.map((s: VersionPathSegment) => path_segment(s.level, s.name)).join("/")}`
    out.set(`${path}|${period}`, value)
  })
  return out
}

/** One level of a leaf's path, with the name as the version spells it. */
export interface VersionPathSegment {
  level: string
  name: string
}

/** A row's kind for the per-cell primary pick — the grid's own rule (`primary_row_kind`). */
function compare_row_kind(row: any): "actual" | "forecast" | "other" {
  const kind = row?.source_kind
  if (kind === "actual") return "actual"
  if (kind === "forecast") return "forecast"
  if (kind == null) return row?.is_actual ? "actual" : "forecast"
  return "other"
}

/**
 * Walk a hierarchy (a raw API response or the grid's own `SalesHierarchyNode` tree — both carry
 * `name`, `level`, `children`, `sales_data`) and report each leaf's primary value per period:
 * actual rows win, then plan `forecast` rows, then anything else, summed within the winning kind.
 * `period_year_shift` realigns another fiscal year onto this version's columns.
 */
export function walk_version_leaf_periods(
  nodes: unknown[],
  period_year_shift: number,
  on_leaf_period: (path: VersionPathSegment[], period: string, value: VersionLeafValue) => void
): void {
  function visit(node: any, parent_path: VersionPathSegment[]): void {
    const path = [...parent_path, { level: String(node?.level ?? ""), name: String(node?.name ?? "").trim() }]
    const children = Array.isArray(node?.children) ? node.children : []
    if (children.length > 0) {
      for (const child of children) visit(child, path)
      return
    }
    // Leaf: bucket rows per period by kind, then actual > forecast > other.
    const buckets = new Map<string, { actual: any[]; forecast: any[]; other: any[] }>()
    for (const row of node?.sales_data ?? []) {
      const period = shift_period_year(row_period_key(row), period_year_shift)
      if (!period) continue
      let bucket = buckets.get(period)
      if (!bucket) {
        bucket = { actual: [], forecast: [], other: [] }
        buckets.set(period, bucket)
      }
      bucket[compare_row_kind(row)].push(row)
    }
    for (const [period, bucket] of buckets) {
      const winning = bucket.actual.length > 0 ? bucket.actual : bucket.forecast.length > 0 ? bucket.forecast : bucket.other
      let volume = 0
      let cases = 0
      let stores = 0
      let units: number | null = null
      for (const row of winning) {
        volume += row_value(row, "volume_dollars")
        cases += row_value(row, "cases_sold")
        stores = Math.max(stores, row_value(row, "store_count"))
        const u = row?.units_sold ?? row?.metrics?.units_sold
        if (u !== undefined && u !== null) units = (units ?? 0) + Number(u)
      }
      on_leaf_period(path, period, { volume_dollars: volume, cases_sold: cases, store_count: stores, units_sold: units })
    }
  }

  for (const node of nodes) visit(node, [])
}

export interface VersionCompareAnnotation {
  nodes: SalesHierarchyNode[]
  /** `path|period` keys of the compared version that found a row in this version. */
  matched_keys: Set<string>
}

function zero_metrics(case_cost: number): SalesMetrics {
  return { store_count: 0, volume_dollars: 0, cases_sold: 0, case_cost }
}

function period_from_key(period_key: string): { year: number; month: number; period_key: string } {
  const year = Number(period_key.slice(0, 4))
  const month = Number(period_key.slice(5, 7))
  return { period_key, year: Number.isFinite(year) ? year : 0, month: Number.isFinite(month) ? month : 0 }
}

/** Annotate the current display tree's leaves with compared-version values. */
export function annotate_hierarchy_with_version_compare(
  roots: SalesHierarchyNode[],
  other: Map<string, VersionLeafValue>
): VersionCompareAnnotation {
  const matched_keys = new Set<string>()

  function annotate_point(dp: SalesDataPoint, other_value: VersionLeafValue | undefined): SalesDataPoint {
    const compared: VersionLeafValue = other_value ?? {
      volume_dollars: 0,
      cases_sold: 0,
      store_count: 0,
      units_sold: null,
    }
    const forecast_metrics: SalesMetrics = {
      ...dp.metrics,
      volume_dollars: compared.volume_dollars,
      cases_sold: compared.cases_sold,
      store_count: compared.store_count,
      units_sold: compared.units_sold,
    }
    const actual_metrics: SalesMetrics = { ...dp.metrics }
    const volume_delta = Number(dp.metrics.volume_dollars || 0) - compared.volume_dollars
    const cases_delta = Number(dp.metrics.cases_sold || 0) - compared.cases_sold
    return {
      ...dp,
      forecast_metrics,
      actual_metrics,
      variance: {
        store_count_variance: Number(dp.metrics.store_count || 0) - compared.store_count,
        volume_dollars_variance: volume_delta,
        cases_sold_variance: cases_delta,
        store_count_variance_pct: 0,
        volume_dollars_variance_pct:
          compared.volume_dollars !== 0 ? (volume_delta / compared.volume_dollars) * 100 : 0,
        cases_sold_variance_pct: compared.cases_sold !== 0 ? (cases_delta / compared.cases_sold) * 100 : 0,
      },
      compare_axis: "dollars" as const,
    }
  }

  function visit(node: SalesHierarchyNode, parent_path: string): SalesHierarchyNode {
    const path = `${parent_path}/${path_segment(node.level, node.name)}`
    const children = (node as { children?: SalesHierarchyNode[] }).children
    if (Array.isArray(children) && children.length > 0) {
      return { ...(node as any), children: children.map((c: SalesHierarchyNode) => visit(c, path)) }
    }

    const primary = new Set(select_primary_sales_rows(node.sales_data))
    const seen_periods = new Set<string>()
    const case_cost = Number((node as any).case_cost || 0)
    let sales_data = (node.sales_data || []).map((dp: SalesDataPoint) => {
      const period = dp.period?.period_key
      if (!period || !primary.has(dp) || seen_periods.has(period)) return dp
      seen_periods.add(period)
      const key = `${path}|${period}`
      const other_value = other.get(key)
      if (other_value) matched_keys.add(key)
      return annotate_point(dp, other_value)
    })

    // Periods the compared version plans but this one does not: inject a zero row so
    // the difference is visible instead of silently absent.
    for (const [key, other_value] of other) {
      if (!key.startsWith(`${path}|`)) continue
      const period = key.slice(path.length + 1)
      if (seen_periods.has(period)) continue
      matched_keys.add(key)
      seen_periods.add(period)
      sales_data = [
        ...sales_data,
        annotate_point(
          {
            period: period_from_key(period),
            metrics: zero_metrics(case_cost),
            is_forecast: true,
          } as SalesDataPoint,
          other_value
        ),
      ]
    }
    sales_data.sort((a: SalesDataPoint, b: SalesDataPoint) =>
      String(a.period?.period_key ?? "").localeCompare(String(b.period?.period_key ?? ""))
    )

    return { ...(node as any), sales_data }
  }

  return { nodes: roots.map((r: SalesHierarchyNode) => visit(r, "root")), matched_keys }
}

/** Distinct compared-version rows (paths) that matched nothing in this version at all. */
export function count_unmatched_compare_paths(
  other: Map<string, VersionLeafValue>,
  matched_keys: Set<string>
): number {
  const unmatched_paths = new Set<string>()
  for (const key of other.keys()) {
    if (!matched_keys.has(key)) {
      unmatched_paths.add(key.slice(0, key.lastIndexOf("|")))
    }
  }
  return unmatched_paths.size
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  const api_tree = [
    {
      name: "Kroger",
      level: "customer",
      children: [
        {
          name: "Sparkling",
          level: "product_line",
          children: [
            {
              name: "Yuzu 12oz",
              level: "sku",
              children: [],
              sales_data: [
                { period: "2027-01", source_kind: "forecast", volume_dollars: "1000", cases_sold: 100, store_count: 10 },
                // Actual for the same cell must win, not add.
                { period: "2027-01", source_kind: "actual", volume_dollars: "900", cases_sold: 90, store_count: 10 },
                { period: "2027-02", source_kind: "forecast", volume_dollars: "1100", cases_sold: 110, store_count: 10 },
              ],
            },
          ],
        },
      ],
    },
  ]

  function current_tree(): SalesHierarchyNode[] {
    return [
      {
        id: "c1",
        name: "Kroger",
        level: "customer",
        sales_data: [],
        children: [
          {
            id: "pl1",
            name: "Sparkling",
            level: "product_line",
            sales_data: [],
            children: [
              {
                id: "s1",
                name: "Yuzu 12oz",
                level: "sku",
                sales_data: [
                  {
                    period: { year: 2027, month: 1, period_key: "2027-01" },
                    metrics: { store_count: 12, volume_dollars: 1200, cases_sold: 120, case_cost: 10 },
                    is_forecast: true,
                    source_kind: "forecast",
                  },
                ],
                children: [],
              },
            ],
          },
        ],
      },
    ] as unknown as SalesHierarchyNode[]
  }

  describe("shift_period_year", () => {
    it("realigns month and week grains and leaves malformed keys alone", () => {
      expect(shift_period_year("2028-01", -1)).toBe("2027-01")
      expect(shift_period_year("2028-W03", -1)).toBe("2027-W03")
      expect(shift_period_year("2026-12", 1)).toBe("2027-12")
      expect(shift_period_year("total", -1)).toBe("total")
      expect(shift_period_year("2028-01", 0)).toBe("2028-01")
    })
  })

  describe("compare_period_year_shift", () => {
    it("is the current minus compared fiscal year, zero when either is unknown", () => {
      expect(compare_period_year_shift(2027, 2028)).toBe(-1)
      expect(compare_period_year_shift(2027, 2027)).toBe(0)
      expect(compare_period_year_shift(undefined, 2028)).toBe(0)
    })
  })

  describe("build_version_leaf_value_map with a year shift", () => {
    it("lands a 2028 plan's months on 2027 keys so cross-year compares stop reading $0", () => {
      const nodes_2028 = [
        {
          name: "Kroger",
          level: "customer",
          children: [],
          sales_data: [
            { period: "2028-01", source_kind: "forecast", volume_dollars: "500", cases_sold: 50, store_count: 5 },
          ],
        },
      ]
      const map = build_version_leaf_value_map(nodes_2028, -1)
      expect(map.get("root/customer:kroger|2027-01")?.volume_dollars).toBe(500)
      expect(map.has("root/customer:kroger|2028-01")).toBe(false)
    })
  })

  describe("build_version_leaf_value_map", () => {
    it("keys leaves by path|period with actual-wins per cell", () => {
      const map = build_version_leaf_value_map(api_tree)
      const jan = map.get("root/customer:kroger/product_line:sparkling/sku:yuzu 12oz|2027-01")!
      expect(jan.volume_dollars).toBe(900)
      expect(jan.cases_sold).toBe(90)
      const feb = map.get("root/customer:kroger/product_line:sparkling/sku:yuzu 12oz|2027-02")!
      expect(feb.volume_dollars).toBe(1100)
    })
  })

  describe("annotate_hierarchy_with_version_compare", () => {
    it("puts the compared version in the forecast slot and this version in the actual slot", () => {
      const map = build_version_leaf_value_map(api_tree)
      const { nodes, matched_keys } = annotate_hierarchy_with_version_compare(current_tree(), map)
      const sku = (nodes[0] as any).children[0].children[0]
      const jan = sku.sales_data.find((dp: any) => dp.period.period_key === "2027-01")
      expect(jan.actual_metrics.volume_dollars).toBe(1200)
      expect(jan.forecast_metrics.volume_dollars).toBe(900)
      expect(jan.variance.volume_dollars_variance).toBe(300)
      expect(jan.compare_axis).toBe("dollars")
      expect(matched_keys.size).toBeGreaterThan(0)
      // Displayed value is untouched — comparing must not change this version's numbers.
      expect(jan.metrics.volume_dollars).toBe(1200)
    })

    it("injects zero rows for periods only the compared version plans", () => {
      const map = build_version_leaf_value_map(api_tree)
      const { nodes } = annotate_hierarchy_with_version_compare(current_tree(), map)
      const sku = (nodes[0] as any).children[0].children[0]
      const feb = sku.sales_data.find((dp: any) => dp.period.period_key === "2027-02")
      expect(feb).toBeDefined()
      expect(feb.metrics.volume_dollars).toBe(0)
      expect(feb.forecast_metrics.volume_dollars).toBe(1100)
      expect(feb.variance.volume_dollars_variance).toBe(-1100)
    })

    it("reports compared rows this version does not have at all", () => {
      const map = build_version_leaf_value_map(api_tree)
      const empty_current = [
        { id: "c-x", name: "Publix", level: "customer", sales_data: [], children: [] },
      ] as unknown as SalesHierarchyNode[]
      const { matched_keys } = annotate_hierarchy_with_version_compare(empty_current, map)
      expect(count_unmatched_compare_paths(map, matched_keys)).toBe(1)
    })
  })
}
